export const POOL_STATUS = {
  ACTIVE: 'active',
  CLOSED: 'closed',
};

/** Pool CRUD: /candidate-talent-pool/pools (same routes, with prefix) */
export const POOL_API_ENABLED = true;

export const DEFAULT_POOLS = [
  {
    id: 'all-candidates',
    name: 'All candidates',
    description: 'Browse your full candidate database',
    status: POOL_STATUS.ACTIVE,
    tags: [],
    memberCount: 0,
  },
];

export const VIEW_MODES = {
  LIST: 'list',
  SPLIT: 'split',
};

export const ROW_DENSITY = {
  COMPACT: 'compact',
  COZY: 'cozy',
  COMFORTABLE: 'comfortable',
};

export const ROW_DENSITY_OPTIONS = [
  { value: ROW_DENSITY.COMPACT, label: 'Compact' },
  { value: ROW_DENSITY.COZY, label: 'Cozy' },
  { value: ROW_DENSITY.COMFORTABLE, label: 'Comfortable' },
];

/** Sort options for pool listing (UserListing-style radio panel) */
export const POOL_SORT_OPTIONS = [
  {
    id: 1,
    label: 'Most recent',
    value: 'most-recent',
    sortvalue: { path: 'createdAt', order: 'DESC' },
  },
  {
    id: 2,
    label: 'Name A→Z',
    value: 'name-asc',
    sortvalue: { path: 'firstName', order: 'ASC' },
  },
  {
    id: 3,
    label: 'Highest rated',
    value: 'highest-rated',
    sortvalue: { path: 'rating', order: 'DESC' },
  },
];

export const STAGE_COLORS = {
  Applied: 'primary',
  Screened: 'warning',
  Shortlisted: 'info',
  Interview: 'purple',
  Declined: 'danger',
};

/** Add-filter menu keys — aligned with CandidateFilterForm + pool extras */
export const ADD_FILTER_KEYS = {
  LOCATION: 'location',
  SOURCE: 'source',
  DATE_ADDED: 'dateAdded',
  LAST_APPLICATION_DATE: 'lastApplicationDate',
  JOB: 'job',
  STAGE_GROUP: 'stageGroup',
  TAGS: 'tags',
  MIN_RATING: 'minRating',
  HAS_CV: 'hasCv',
  HAS_EMAIL: 'hasEmail',
  HAS_PHONE: 'hasPhone',
};

export const ADD_FILTER_CATEGORIES = [
  { key: ADD_FILTER_KEYS.LOCATION, label: 'Location' },
  { key: ADD_FILTER_KEYS.SOURCE, label: 'Source' },
  { key: ADD_FILTER_KEYS.DATE_ADDED, label: 'Added On' },
  { key: ADD_FILTER_KEYS.LAST_APPLICATION_DATE, label: 'Last Application Date' },
  { key: ADD_FILTER_KEYS.JOB, label: 'Job' },
  { key: ADD_FILTER_KEYS.STAGE_GROUP, label: 'Stage Group' },
  { key: ADD_FILTER_KEYS.TAGS, label: 'Tags' },
  { key: ADD_FILTER_KEYS.MIN_RATING, label: 'Rating' },
];

export const ADD_FILTER_QUICK_TOGGLES = [
  { key: ADD_FILTER_KEYS.HAS_EMAIL, label: 'Has email', icon: 'email' },
  { key: ADD_FILTER_KEYS.HAS_PHONE, label: 'Has phone', icon: 'phone' },
];

const ADD_FILTER_LABELS = Object.fromEntries(
  [...ADD_FILTER_CATEGORIES, ...ADD_FILTER_QUICK_TOGGLES].map((item) => [
    item.key,
    item.label,
  ]),
);

export const getAddFilterLabel = (key, fallback = key) =>
  ADD_FILTER_LABELS[key] || fallback;

export const LOCATION_RADIUS_OPTIONS = [
  '0',
  '5',
  '10',
  '15',
  '20',
  '25',
  '30',
  '35',
  '40',
  '45',
  '50',
];

/** Stage colors for listing dots / badges */
export const STAGE_FILTER_OPTIONS = [
  { value: 'Applied', label: 'Applied', color: '#9CA3AF' },
  { value: 'Filtered', label: 'Filtered', color: '#9CA3AF' },
  { value: 'Screened', label: 'Screened', color: '#EAB308' },
  { value: 'Shortlisted', label: 'Shortlisted', color: '#8B5CF6' },
  { value: 'Interview', label: 'Interview', color: '#3B82F6' },
  { value: 'Offer', label: 'Offer', color: '#14B8A6' },
  { value: 'Hired', label: 'Hired', color: '#15803D' },
  { value: 'Declined', label: 'Declined', color: '#F43F5E' },
];

/** Same options as CandidateFilterForm userRating */
export const RATING_FILTER_OPTIONS = [
  { value: '5', label: '5', stars: 5 },
  { value: '4', label: '4 & above', stars: 4 },
  { value: '3', label: '3 & above', stars: 3 },
  { value: '2', label: '2 & above', stars: 2 },
  { value: '1', label: '1 & above', stars: 1 },
];

export const EMPTY_STATE_COPY = {
  pools: {
    title: 'No candidate pools yet',
    description: 'Create a pool to organise and track groups of candidates for specific roles or campaigns.',
    action: 'Create pool',
  },
  candidates: {
    title: 'No candidates in this pool',
    description: 'Add candidates from your database or search results to start building this pool.',
    action: 'Add candidates',
  },
  search: {
    title: 'No matches found',
    description: 'Try adjusting your search or filters to find candidates.',
  },
  error: {
    title: 'Unable to load candidates',
    description: 'Something went wrong while fetching candidates. Please try again.',
    action: 'Try again',
  },
};

export const TOAST_MESSAGES = {
  poolCreated: 'Pool created successfully.',
  poolUpdated: 'Pool updated successfully.',
  poolDeleted: 'Pool deleted successfully.',
  poolClosed: 'Pool closed successfully.',
  candidateAdded: 'Candidate added to pool.',
  candidateRemoved: 'Candidate removed from pool.',
  refreshError: 'Unable to refresh data. Please try again.',
  saveFilterRequired: 'Apply at least one filter from Add filter before saving.',
  aiScoreWeightsSaved: 'AI score weights saved.',
  aiScoreWeightsReset: 'AI score weights reset.',
};

/** Hex color: #rgb | #rrggbb | #rrggbbaa */
export const POOL_HEX_COLOR_RE =
  /^#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

/** Color swatches for Add/Edit Pool modal (matches design) */
export const POOL_COLOR_OPTIONS = [
  '#C4B5FD',
  '#A78BFA',
  '#F9A8D4',
  '#F87171',
  '#FB923C',
  '#FBBF24',
  '#4ADE80',
  '#22D3EE',
  '#60A5FA',
  '#94A3B8',
];

export const POOL_FORM_INITIAL = {
  name: '',
  description: '',
  color: '', // optional hex (#fff, #4F46E5, #4F46E5CC)
  tags: [],
};

export const FILTER_FORM_INITIAL = {
  query: '',
  stage: '',
  source: '',
  minRating: '',
  tags: [],
};

export const PAGINATION_ACCESS_KEY = 'candidatePoolingPg';

export const ALL_CANDIDATES_POOL_ID = 'all-candidates';

/** Max height of each sidebar list (filters / pools / tags). */
export const SIDEBAR_SECTION_MAX_HEIGHT = 176;
/** Page size for sidebar scroll pagination. */
export const SIDEBAR_LIST_PER_PAGE = 10;

/** Max candidates that can be selected at once for bulk actions. */
export const MAX_SELECTED_CANDIDATES = 10;

/** Each AI score dimension is 0–100, so the combined scale is 300. */
export const AI_SCORE_WEIGHT_MAX_TOTAL = 300;

/** Default AI score dimension weights (each out of 100) — matches GET /company/ai-score-weights */
export const DEFAULT_AI_SCORE_WEIGHTS = {
  technicalFit: 40,
  experienceRelevance: 35,
  culturalMatch: 25,
};

export const AI_SCORE_WEIGHT_FIELDS = [
  {
    key: 'technicalFit',
    label: 'Technical fit',
    description: 'Skills & CV match against the role',
  },
  {
    key: 'experienceRelevance',
    label: 'Experience relevance',
    description: 'Years, seniority and domain overlap',
  },
  {
    key: 'culturalMatch',
    label: 'Cultural match',
    description: 'Values, tone and soft-signal alignment',
  },
];
